'use client';

import { useState } from 'react';

import type { OrderDocument } from '@eliadmin/core/src/api/customers/customers-types';
import { downloadFileFromBlob } from '@eliadmin/core/src/api/general/general-api';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';

/**
 * „Faktúra (PDF)“ (DLZ `use-order-document.ts`): the order's first document, downloaded through
 * the customer's token like the core `ProfileOrderDownloadsDialog`. No document → no action (an
 * order the shop has not invoiced yet, a cancelled one).
 */
export function useOrderDocument(
  documents: Pick<OrderDocument, 'id' | 'name' | 'number'>[] | null | undefined,
) {
  const authToken = useCustomerStore(s => s.me?.authToken);
  const locale = useLocaleStore(s => s.currentLocale);
  const [loading, setLoading] = useState(false);
  const document = authToken ? documents?.[0] : undefined;

  const download = async () => {
    if (!document || !authToken) return;
    setLoading(true);
    try {
      await downloadFileFromBlob({
        fileId: document.id,
        authToken,
        locale,
        fileName: [document.name, document.number].filter(Boolean).join('_') || String(document.id),
      });
    } finally {
      setLoading(false);
    }
  };

  return { document, download, loading };
}
